import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import RecordListingCard from './RecordListingCard';
import { pickNonEmptyBoundary } from '../../utils/enrichSharedLotsForMap';
import { usePrefetchLotBoundaries } from '../../hooks/usePrefetchLotBoundaries';

/**
 * Listado de cards por registro (una por lote, UF o lote CAD). Recibe los items
 * ya filtrados, ordenados y recortados al chunk visible. Sin portada, la media
 * es la silueta (`LotShapeThumbnail`): primero `boundary` de la card (getmyrecords),
 * si no la del lote o el perímetro por `/getalllots` sin JWT, solo de los items visibles.
 *
 * @param {{
 *   items: object[],
 *   layout?: 'list'|'grid',
 *   isItemLocated?: Function,
 *   getActions?: Function,
 *   getFooterExtra?: Function,
 *   onOpen?: Function,
 *   onHoverLot?: Function,
 * }} props
 */
export default function RecordCardList({
  items,
  layout = 'list',
  isItemLocated,
  getActions,
  getFooterExtra,
  onOpen,
  onHoverLot,
  /** Lote bajo el cursor del mapa: marca todas las cards de ese lote. */
  highlightedLotId = null,
}) {
  const lotIdsNeedingShape = useMemo(() => {
    const seen = new Set();
    const ids = [];
    (items || []).forEach((item) => {
      if (item.coverImage || !item.lotId) return;
      if (pickNonEmptyBoundary(item.boundary, item.lot?.boundary, item.unit?.boundary)) return;
      const id = String(item.lotId);
      if (seen.has(id)) return;
      seen.add(id);
      ids.push(id);
    });
    return ids;
  }, [items]);

  const boundaryByLotId = usePrefetchLotBoundaries(lotIdsNeedingShape);

  const resolveBoundary = (item) => {
    // Con foto la silueta es solo el respaldo si la imagen no carga: la que ya
    // viene en los datos, sin pedir perímetros extra.
    if (item.coverImage) return pickNonEmptyBoundary(item.boundary, item.lot?.boundary, item.unit?.boundary);
    // CAD (sin lote catastral): solo la silueta que trae la card (`boundary` de getmyrecords).
    if (!item.lotId) return pickNonEmptyBoundary(item.boundary);
    return pickNonEmptyBoundary(
      item.boundary,
      item.lot?.boundary,
      item.unit?.boundary,
      boundaryByLotId.get(String(item.lotId)),
    );
  };

  const isGrid = layout === 'grid';

  return (
    <Box
      data-testid="record-card-list"
      data-layout={layout}
      sx={isGrid
        ? {
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          alignItems: 'stretch',
          gap: 1,
        }
        : { display: 'flex', flexDirection: 'column', gap: 1 }}
    >
      {(items || []).map((item) => (
        <Box
          key={item.key}
          data-testid="record-card-cell"
          data-list-lot-id={item.lotId != null ? String(item.lotId) : undefined}
          data-records-source={item.record?.records_source || undefined}
          sx={isGrid ? { minWidth: 0, display: 'flex', flexDirection: 'column' } : undefined}
        >
          <RecordListingCard
            item={item}
            layout={isGrid ? 'grid' : 'list'}
            isLocated={Boolean(isItemLocated?.(item))}
            actions={getActions ? getActions(item) : []}
            footerExtra={getFooterExtra ? getFooterExtra(item) : null}
            reserveFooterExtra={Boolean(getFooterExtra)}
            boundary={resolveBoundary(item)}
            onOpen={onOpen}
            onHover={onHoverLot && item.lotId
              ? (active) => onHoverLot(active ? { lotId: item.lotId, item } : null)
              : undefined}
            isHighlighted={highlightedLotId != null && String(item.lotId) === String(highlightedLotId)}
          />
        </Box>
      ))}
    </Box>
  );
}
